<script>
import CancelToken from 'axios/lib/cancel/CancelToken'
import consumer from 'channels/consumer'
import { useCancellable } from 'frontend/_hooks/use-cancellable'
import { getEventOrganizationUser } from 'frontend/common/access-helpers'
import SystemNotificationModal from 'frontend/system-notifications/system-notification-modal.vue'
import { ref } from 'vue'

import SystemNotificationsDropdownList from './_system-notifications-dropdown-list.vue'

const VERBOSE_SUBSCRIPTION = false

export default {
  name: 'SystemNotificationsIndicator',
  data() {
    return {
      unreadNotificationsCounter: 0,
      subscription: null,
      reportsSubscription: null,
      refetchInProgress: false,
      nextRefetchPending: false,
      refetchWatchTimeout: null,
      shownNotification: null,
    }
  },
  setup() {
    const windowWidth = ref(window.innerWidth)
    return { ...useCancellable(), windowWidth }
  },
  computed: {
    currentEventOrganizationUser() {
      return getEventOrganizationUser(this.$store.state.session.user, this.$route.params?.eventSlug)
    },
    currentEvent() {
      return this.$store.state.currentContext.event
    },
    currentCluster() {
      return this.$store.state.currentContext.cluster
    },
    currentStagingArea() {
      return this.$store.state.currentContext.stagingArea
    },
    currentChannel() {
      if (this.currentStagingArea) {
        return 'UserUnreadSystemNotificationsStagingAreaChannel'
      } else if (this.currentCluster) {
        return 'UserUnreadSystemNotificationsClusterChannel'
      } else if (this.currentEvent) {
        return 'UserUnreadSystemNotificationsChannel'
      } else {
        return null
      }
    },
    reportsChannel() {
      return 'ReportsReadyChannel'
    },
    reportsChannelParams() {
      return {
        event_organization_user_id: this.currentEventOrganizationUser.id,
        event_id: this.currentEvent?.id,
        fields: ['download_url'],
      }
    },
    channelParams() {
      const result = {}
      if (this.currentEvent) {
        result.event_id = this.currentEvent.id
      }
      if (this.currentCluster) {
        result.cluster_id = this.currentCluster.id
      } else if (this.currentStagingArea) {
        result.staging_area_id = this.currentStagingArea.id
      }
      return result
    },
    isLargeDevice() {
      return this.windowWidth > 1366 // tablets
    },
  },
  watch: {
    reportsChannelParams() {
      clearTimeout(this.refetchWatchTimeout)
      this.refetchWatchTimeout = setTimeout(() => {
        this.subscribeToReportsChannel()
      }, 150)
    },
    currentChannel() {
      clearTimeout(this.refetchWatchTimeout)
      this.refetchWatchTimeout = setTimeout(() => {
        this.refetchCounterAndSubscribe()
      }, 150)
    },
    channelParams() {
      clearTimeout(this.refetchWatchTimeout)
      this.refetchWatchTimeout = setTimeout(() => {
        this.refetchCounterAndSubscribe()
      }, 150)
    },
  },
  mounted() {
    this.refetchCounterAndSubscribe()
    this.subscribeToReportsChannel()
    this.$eventHub.$on('windowResize', this.setWindowWidth)
  },
  unmounted() {
    this.unsubscribeFromReportsChannelIfCan()
    this.unsubscribeIfCan()
    this.$eventHub.$off('windowResize', this.setWindowWidth)
  },
  methods: {
    setWindowWidth() {
      this.windowWidth = window.innerWidth
    },
    readAll() {
      this.axios({
        method: 'post',
        url: `events/${this.currentEvent.slug}/system_notifications/read_all`,
        data: this.channelParams,
        cancelToken: new CancelToken(cancel => {
          this.cancellable['read-all'] = cancel
        }),
      })
        .then(() => {
          this.$success({ message: 'All notifications successfully marked as read' })
        })
        .catch(() => {
          this.$error({ message: 'Cannot mark all notifications as read' })
        })
    },
    // Download a file form a url.
    saveFile(url) {
      return new Promise((resolve, reject) => {
        // Get file name from url.
        const xhr = new XMLHttpRequest()
        xhr.responseType = 'blob'
        xhr.onload = () => {
          resolve(xhr)
        }
        xhr.onerror = reject
        xhr.open('GET', url)
        xhr.send()
      }).then(xhr => {
        let filename = ''
        const disposition = xhr.getResponseHeader('Content-Disposition')
        if (disposition && disposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          const matches = filenameRegex.exec(disposition)
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '')
          }
        }
        // const filename = url.substring(url.lastIndexOf('/') + 1).split('?')[0]
        const a = document.createElement('a')
        a.href = window.URL.createObjectURL(xhr.response) // xhr.response is a blob
        a.download = filename // Set the file name.
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        return xhr
      })
    },
    subscribeToReportsChannel() {
      this.unsubscribeFromReportsChannelIfCan()
      if (!this.currentEventOrganizationUser) {
        return
      }
      this.reportsSubscription = consumer.subscriptions.create(
        {
          channel: this.reportsChannel,
          ...this.reportsChannelParams,
        },
        {
          customIdentifier: Math.round(Math.random() * 1000000),
          connected: () => {
            if (VERBOSE_SUBSCRIPTION)
              console.log(`%c CONNECTED TO ${this.reportsChannel}`, 'color: #4c74b9')
          },
          disconnected: () => {
            if (VERBOSE_SUBSCRIPTION) console.warn(`DISCONNECTED FROM ${this.reportsChannel}`)
          },
          rejected: () => {
            console.error(`REJECTED CONNECTION TO ${this.reportsChannel}`)
          },
          received: message => {
            if (VERBOSE_SUBSCRIPTION)
              console.log(`%c ${this.reportsChannel} message came in:`, 'color: gray', message)
            if (!document.hidden) {
              const msg = JSON.parse(message.obj)
              const downloadUrl =
                msg?.entities?.items?.[Object.keys(msg.entities.items)?.[0]]?.download_url
              if (downloadUrl) {
                this.saveFile(downloadUrl).then(() => {
                  this.$success({ message: 'Report downloaded successfully' })
                })
              }
            } else {
              console.log('PAGE IS NOT ACTIVE, WONT PROMPT FOR DOWNLOAD')
            }
          },
        },
      )
    },
    refetchCounterAndSubscribe() {
      if (!this.currentEvent?.slug) {
        this.unsubscribeIfCan()
        return
      }
      if (this.refetchInProgress) {
        this.nextRefetchPending = true
        return
      }
      this.refetchInProgress = true
      this.nextRefetchPending = false
      this.unsubscribeIfCan()
      this.axios({
        method: 'post',
        url: `events/${this.currentEvent.slug}/system_notifications/unread_counter`,
        data: this.channelParams,
        cancelToken: new CancelToken(cancel => {
          this.cancellable['unread-notifications-counter'] = cancel
        }),
      })
        .then(response => {
          this.unreadNotificationsCounter = response.data.counter
          this.subscription = consumer.subscriptions.create(
            {
              channel: this.currentChannel,
              ...this.channelParams,
            },
            {
              customIdentifier: Math.round(Math.random() * 1000000),
              connected: () => {
                if (VERBOSE_SUBSCRIPTION)
                  console.log(`%c CONNECTED TO ${this.currentChannel}`, 'color: #4c74b9')
              },
              disconnected: () => {
                if (VERBOSE_SUBSCRIPTION) console.warn(`DISCONNECTED FROM ${this.currentChannel}`)
              },
              rejected: () => {
                console.error(`REJECTED CONNECTION TO ${this.currentChannel}`)
              },
              received: message => {
                if (VERBOSE_SUBSCRIPTION)
                  console.log(`%c ${this.currentChannel} message came in:`, 'color: gray', message)
                this.unreadNotificationsCounter = message
                this.$eventHub.$emit('refetch-notifications')
              },
            },
          )
        })
        .catch(this.handleCancel)
        .finally(() => {
          this.refetchInProgress = false
          if (this.nextRefetchPending) {
            this.refetchCounterAndSubscribe()
          }
        })
    },
    unsubscribeIfCan() {
      this.unreadNotificationsCounter = 0
      if (this.subscription) {
        const remoteSubscription = consumer.subscriptions.subscriptions.find(
          s => s.customIdentifier == this.subscription.customIdentifier,
        )
        if (remoteSubscription) {
          if (VERBOSE_SUBSCRIPTION)
            console.log(
              `%c unreadSystemNotificationsCounter will unsubscribe from its channel`,
              'color: gray',
            )
          consumer.subscriptions.remove(remoteSubscription)
        }
        this.subscription = null
      }
    },
    unsubscribeFromReportsChannelIfCan() {
      if (this.reportsSubscription) {
        const remoteSubscription = consumer.subscriptions.subscriptions.find(
          s => s.customIdentifier == this.reportsSubscription.customIdentifier,
        )
        if (remoteSubscription) {
          consumer.subscriptions.remove(remoteSubscription)
        }
        this.reportsSubscription = null
      }
    },
  },
  components: {
    SystemNotificationsDropdownList,
    SystemNotificationModal,
  },
}
</script>
<template lang="pug">
dropdown.system-notifications-indicator(:alignRight="isLargeDevice" scrollable)
  template(#default="{ isOpen }")
    .header-indicator.d-flex.align-items-center.justify-content-center(
      :class="{ 'is-open': isOpen }"
    )
      i(
        :class="{ 'mi-outlined mi-notifications ring': unreadNotificationsCounter, 'mi-outlined mi-notifications-off': !unreadNotificationsCounter }"
        name="bellNotificator"
      )
      .addon(v-if="!!unreadNotificationsCounter") {{ unreadNotificationsCounter > 99 ? '99+' : unreadNotificationsCounter }}
  template(#items="{ isOpened }")
    li.d-flex.align-items-center.justify-content-end.pt-0.pb-0(v-if="!!unreadNotificationsCounter")
      a.d-inline-flex.small.w-auto.pe-2(@click="readAll($event)") Mark all as read
    system-notifications-dropdown-list(v-if="isOpened" @openModal="shownNotification = $event")

template(v-if="shownNotification")
  system-notification-modal(:item="shownNotification" @closed="shownNotification = null")
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;
.system-notifications-indicator {
  color: map.get(themeVariables.$colors, onSurfaceVariant);
  .addon {
    top: -4px;
    bottom: auto;
    left: calc(100% - 18px);
    right: auto;
  }
}
</style>
